/**
 * StateMachine - Manages application state transitions
 *
 * State flow:
 * UNINITIALIZED -> LOADING -> READY -> PLAYING <-> PAUSED/CHECKING/REVEALING -> COMPLETE -> LOADING
 *                     ↓                                                             ↓
 *                   ERROR -------------------------------------------------> LOADING
 */
export class StateMachine {
  /**
   * @param {Object} config - State configuration object
   * @param {string[]} config.states - Array of state names
   * @param {number[][]} config.transitions - Transition matrix (1=allowed, 0=blocked)
   * @param {string} config.initialState - Starting state
   * @param {Object} config.description - State descriptions
   */
  constructor(config) {
    this.states = config.states;
    this.transitions = config.transitions;
    this.stateDescriptions = config.description || {};

    // Create state index lookup
    this.stateIndex = {};
    this.states.forEach((state, index) => {
      this.stateIndex[state] = index;
    });

    // Set initial state
    this.currentState = config.initialState || this.states[0];
    this.previousState = null;

    // State history
    this.history = [this.currentState];
    this.maxHistoryLength = 50;

    // Event callbacks
    this.listeners = {
      onEnter: {}, // Callbacks when entering a state
      onExit: {}, // Callbacks when exiting a state
      onTransition: [], // Callbacks on any transition
    };

    // Validation
    this._validateConfig();
  }

  /**
   * Validate configuration
   * @private
   */
  _validateConfig() {
    if (!this.states || this.states.length === 0) {
      throw new Error("StateMachine: states array cannot be empty");
    }

    if (!this.transitions || this.transitions.length !== this.states.length) {
      throw new Error(
        "StateMachine: transitions matrix must match states length"
      );
    }

    this.transitions.forEach((row, i) => {
      if (row.length !== this.states.length) {
        throw new Error(`StateMachine: transition row ${i} length mismatch`);
      }
    });

    if (!this.states.includes(this.currentState)) {
      throw new Error(
        `StateMachine: initial state "${this.currentState}" not in states list`
      );
    }
  }

  /**
   * Get current state
   * @returns {string} Current state name
   */
  getState() {
    return this.currentState;
  }

  /**
   * Get previous state
   * @returns {string|null} Previous state name
   */
  getPreviousState() {
    return this.previousState;
  }

  /**
   * Get state description
   * @param {string} [state] - State to describe (defaults to current)
   * @returns {string} State description
   */
  getDescription(state = this.currentState) {
    return this.stateDescriptions[state] || "No description available";
  }

  /**
   * Check if transition is allowed
   * @param {string} fromState - Starting state
   * @param {string} toState - Target state
   * @returns {boolean} True if transition is allowed
   */
  canTransition(fromState, toState) {
    const fromIndex = this.stateIndex[fromState];
    const toIndex = this.stateIndex[toState];

    if (fromIndex === undefined || toIndex === undefined) {
      return false;
    }

    return this.transitions[fromIndex][toIndex] === 1;
  }

  /**
   * Transition to a new state
   * @param {string} newState - Target state
   * @param {Object} [data] - Optional data to pass to callbacks
   * @returns {boolean} True if transition successful
   * @throws {Error} If transition is not allowed
   */
  transition(newState, data = {}) {
    if (!this.states.includes(newState)) {
      throw new Error(`StateMachine: unknown state "${newState}"`);
    }

    if (this.currentState === newState) {
      console.warn(`StateMachine: already in state "${newState}"`);
      return true;
    }

    if (!this.canTransition(this.currentState, newState)) {
      const error = `StateMachine: transition from "${this.currentState}" to "${newState}" not allowed`;
      console.error(error);
      throw new Error(error);
    }

    // Execute transition
    const oldState = this.currentState;

    // Call onExit callbacks
    this._triggerCallbacks("onExit", oldState, {
      from: oldState,
      to: newState,
      data,
    });

    // Update state
    this.previousState = oldState;
    this.currentState = newState;

    // Update history
    this.history.push(newState);
    if (this.history.length > this.maxHistoryLength) {
      this.history.shift();
    }

    // Call onEnter callbacks
    this._triggerCallbacks("onEnter", newState, {
      from: oldState,
      to: newState,
      data,
    });

    // Call onTransition callbacks
    this._triggerCallbacks("onTransition", null, {
      from: oldState,
      to: newState,
      data,
    });

    return true;
  }

  /**
   * Register callback for entering a state
   * @param {string} state - State name
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  onEnter(state, callback) {
    if (!this.states.includes(state)) {
      throw new Error(`StateMachine: unknown state "${state}"`);
    }

    if (!this.listeners.onEnter[state]) {
      this.listeners.onEnter[state] = [];
    }

    this.listeners.onEnter[state].push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.onEnter[state].indexOf(callback);
      if (index > -1) {
        this.listeners.onEnter[state].splice(index, 1);
      }
    };
  }

  /**
   * Register callback for exiting a state
   * @param {string} state - State name
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  onExit(state, callback) {
    if (!this.states.includes(state)) {
      throw new Error(`StateMachine: unknown state "${state}"`);
    }

    if (!this.listeners.onExit[state]) {
      this.listeners.onExit[state] = [];
    }

    this.listeners.onExit[state].push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.onExit[state].indexOf(callback);
      if (index > -1) {
        this.listeners.onExit[state].splice(index, 1);
      }
    };
  }

  /**
   * Register callback for any state transition
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  onTransition(callback) {
    this.listeners.onTransition.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.onTransition.indexOf(callback);
      if (index > -1) {
        this.listeners.onTransition.splice(index, 1);
      }
    };
  }

  /**
   * Trigger callbacks for a specific event
   * @private
   */
  _triggerCallbacks(eventType, state, eventData) {
    if (eventType === "onTransition") {
      this.listeners.onTransition.forEach((callback) => {
        try {
          callback(eventData);
        } catch (error) {
          console.error("StateMachine: callback error", error);
        }
      });
    } else {
      const callbacks = this.listeners[eventType][state] || [];
      callbacks.forEach((callback) => {
        try {
          callback(eventData);
        } catch (error) {
          console.error("StateMachine: callback error", error);
        }
      });
    }
  }

  /**
   * Get all possible transitions from current state
   * @returns {string[]} Array of possible next states
   */
  getPossibleTransitions() {
    const fromIndex = this.stateIndex[this.currentState];
    const possible = [];

    this.transitions[fromIndex].forEach((allowed, toIndex) => {
      if (allowed === 1) {
        possible.push(this.states[toIndex]);
      }
    });

    return possible;
  }

  /**
   * Get state transition history
   * @param {number} [limit] - Maximum number of history items to return
   * @returns {string[]} Array of state names in chronological order
   */
  getHistory(limit) {
    if (limit) {
      return this.history.slice(-limit);
    }
    return [...this.history];
  }

  /**
   * Check if in a specific state
   * @param {string} state - State to check
   * @returns {boolean} True if currently in the specified state
   */
  is(state) {
    return this.currentState === state;
  }

  /**
   * Check if in any of the specified states
   * @param {string[]} states - States to check
   * @returns {boolean} True if currently in any of the specified states
   */
  isAnyOf(states) {
    return states.includes(this.currentState);
  }

  /**
   * Reset state machine to initial state
   * @param {boolean} [clearHistory=false] - Whether to clear history
   */
  reset(clearHistory = false) {
    const initialState = this.states[0];
    if (this.currentState !== initialState) {
      this.previousState = this.currentState;
      this.currentState = initialState;

      if (clearHistory) {
        this.history = [initialState];
      } else {
        this.history.push(initialState);
      }
    }
  }

  /**
   * Generate a visual representation of the state machine
   * @returns {string} ASCII art representation
   */
  visualize() {
    let output = "\n=== State Machine ===\n";
    output += `Current: ${this.currentState}\n`;
    output += `Previous: ${this.previousState || "none"}\n\n`;

    output += "Transition Matrix:\n";
    output +=
      "     " +
      this.states.map((s) => s.substring(0, 4).padEnd(4)).join(" ") +
      "\n";

    this.transitions.forEach((row, i) => {
      const stateName = this.states[i].substring(0, 4).padEnd(4);
      const marker = this.states[i] === this.currentState ? "→" : " ";
      output +=
        `${marker}${stateName} ` +
        row.map((v) => (v ? " ✓  " : " ✗  ")).join(" ") +
        "\n";
    });

    output +=
      "\nPossible transitions: " +
      this.getPossibleTransitions().join(", ") +
      "\n";

    return output;
  }
}
